import type { UserId } from "@core/domains/identity";
import { singleton } from "@core/foundation/class";
import { getBrandedString } from "@core/foundation/string";
import type { PK, SK } from "@core/ports/json-storage";
import { SK_VERSION_STRING } from "@core/ports/json-storage";
import type { ZodUnwrap, z } from "@core/zod";

import { BaseDeviceRepository } from "@react/ionic/storage";

import type { GridEpochId } from "@apis/calorie-grid/models";
import { GridConfig } from "@apis/calorie-grid/models";

import { deviceStorage } from "../bootstrap/device-storage.ts";
import { SQUARE_KCAL_DEFAULT } from "../bootstrap/grid.ts";
import { TABLE_NAME } from "../bootstrap/storage.ts";

const identifierFields = { gridEpochId: true, userId: true } as const;

const GridConfigIdentifierSchema = GridConfig.schema.pick(identifierFields);

type GridConfigIdentifier = z.infer<typeof GridConfigIdentifierSchema>;

const GridConfigDataSchema = GridConfig.schema.omit(identifierFields);

class GridConfigRepository extends BaseDeviceRepository<
  ZodUnwrap<typeof GridConfigIdentifierSchema>,
  ZodUnwrap<typeof GridConfigDataSchema>,
  GridConfig,
  "grid-config"
> {
  public static readonly getInstance = singleton(
    () => new GridConfigRepository(),
  );

  private constructor() {
    super({
      dataSchema: GridConfigDataSchema,
      deviceStorage,
      entityType: "grid-config",
      identifierSchema: GridConfigIdentifierSchema,
      readSchema: GridConfig.readSchema,
      resourceName: "Grid config",
      tableName: TABLE_NAME,
    });
  }

  public async ensureConfig(
    userId: UserId,
    gridEpochId: GridEpochId,
  ): Promise<GridConfig> {
    return this.getOrCreate({ gridEpochId, userId }, () =>
      GridConfig.create({
        gridEpochId,
        squareKcal: SQUARE_KCAL_DEFAULT,
        userId,
      }),
    );
  }

  protected getPK({
    gridEpochId,
    userId,
  }: Pick<GridConfigIdentifier, "gridEpochId" | "userId">): PK<
    typeof this.entityType
  > {
    return getBrandedString(`${this.entityType}#${userId}#${gridEpochId}`);
  }

  protected getSK(): SK {
    return getBrandedString(SK_VERSION_STRING.v0);
  }
}

export const gridConfigRepository = GridConfigRepository.getInstance();
